/* This is the root JavaScript file for client-side scripts. */
const JSZip = require('jszip')
const commonmark = require('commonform-commonmark')
const docx = require('commonform-docx')
const docxOptions = require('./docx-options')
const fileSaver = require('file-saver')
const termSheetView = require('./term-sheet-view')
const mustache = require('mustache')
const ooxmlSignaturePages = require('ooxml-signature-pages')
const prepareBlanks = require('commonform-prepare-blanks')

const blanksValues = require('./blanks.json')
const orderTemplate = require('./order.json')
const presets = require('./presets.json')
const questions = require('./questions.json')
const signatures = require('./signatures.json')
const webpageTemplate = require('./webpage.json')
const termSheetTemplate = require('./term-sheet')
const termsTemplate = require('./terms.json')
const titles = require('./titles')

const version = require('./version.json')
const displayVersion = version === 'Development Preview' ? version : `Version ${version}`
const website = 'fastpathlicense.com'

const orderFileName = 'Order.docx'
const termsFileName = 'Terms.docx'
const termSheetFileName = 'Term Sheet.docx'

const answers = window.answers = {/* questionName -> null | answerName */}
const fieldsets = {/* questionName => fieldset */}
const inputs = {/* questionName -> [input] */}
let submitButton

document.addEventListener('DOMContentLoaded', () => {
  const main = document.querySelector('body > main')
  const fragment = document.createDocumentFragment()

  // Helper Function
  function append (type, children, attributes = {}) {
    const element = document.createElement(type)
    if (typeof children === 'string') {
      element.appendChild(document.createTextNode(children))
    } else {
      for (const child of children) {
        if (typeof child === 'string') {
          element.appendChild(document.createTextNode(child))
        } else {
          element.appendChild(child)
        }
      }
    }
    for (const key in attributes) {
      element.setAttribute(key, attributes[key])
    }
    fragment.appendChild(element)
  }

  // Version
  append('h2', window.version)

  append('p', 'Complete this form create a ZIP archive with an order form template and legal terms for a commercial software license agreement.')

  // Presets
  append('h3', 'Presets')
  const questionsID = 'questions'
  append('p', [
    'Choose a preset or ',
    (() => {
      const a = document.createElement('a')
      a.href = `#${questionsID}`
      a.appendChild(document.createTextNode(' jump straight to the questions.'))
      return a
    })()
  ])

  const presetsTable = document.createElement('table')
  for (const presetName in presets) {
    const tr = document.createElement('tr')

    const nameTH = document.createElement('th')
    nameTH.appendChild(document.createTextNode(presetName))
    tr.appendChild(nameTH)

    const notesTD = document.createElement('td')
    notesTD.appendChild(document.createTextNode(presets[presetName].notes.join(', ')))
    tr.appendChild(notesTD)

    const buttonTD = document.createElement('td')
    const button = document.createElement('button')
    button.appendChild(document.createTextNode('Apply and Review'))
    button.dataset.preset = presetName
    button.addEventListener('click', onPresetSelect)
    buttonTD.appendChild(button)
    tr.appendChild(buttonTD)

    presetsTable.appendChild(tr)
  }
  function onPresetSelect (event) {
    const presetName = event.target.dataset.preset
    const preset = presets[presetName]
    if (!preset) return
    resetForm()
    for (const [questionName, answerName] of Object.entries(preset.questions)) {
      select(questionName, answerName)
    }
    document.getElementById(questionsID).scrollIntoView({ behavior: 'smooth' })
  }
  fragment.appendChild(presetsTable)

  append('h3', 'Questions', { id: questionsID })
  append('p', 'Try to answer the questions in order. New questions may appear as you give answers.')

  // Main Form
  const form = document.createElement('form')
  fragment.appendChild(form)

  function select (questionName, answerName) {
    const input = document.querySelector(`input[data-question="${questionName}"][data-answer="${answerName}"]`)
    input.checked = true
    onInputChange()
  }

  function resetForm () {
    form.reset()
    for (const question of questions) {
      const defaultAnswer = question.answers.find(answer => answer.default)
      if (!defaultAnswer) continue
      select(question.name, defaultAnswer.name)
    }
  }

  // Advanced Questions
  const haveAdvancedQuestions = questions.some(question => question.advanced)
  let advancedQuestions
  if (haveAdvancedQuestions) {
    advancedQuestions = document.createElement('details')
    const summary = document.createElement('summary')
    advancedQuestions.appendChild(summary)
    summary.appendChild(document.createTextNode('Advanced Questions'))
  }

  // Questions
  for (const { name: questionName, text, advanced, requirements, answers, after, guide } of questions) {
    inputs[questionName] = []
    const hasRequirements = Array.isArray(requirements)

    const set = document.createElement('fieldset')
    if (haveAdvancedQuestions && advanced) advancedQuestions.appendChild(set)
    else form.appendChild(set)
    fieldsets[questionName] = set
    if (hasRequirements) set.classList.add('hidden')

    const legend = document.createElement('legend')
    set.appendChild(legend)
    legend.appendChild(document.createTextNode(questionName))

    const textParagraph = document.createElement('p')
    textParagraph.className = 'question'
    textParagraph.appendChild(document.createTextNode(text))
    set.appendChild(textParagraph)

    for (const answer of answers) {
      const answerName = answer.name

      const label = document.createElement('label')
      set.appendChild(label)

      const input = document.createElement('input')
      input.dataset.question = questionName
      input.dataset.answer = answerName
      input.type = 'radio'
      input.name = questionName
      input.value = answerName
      input.required = hasRequirements
      input.addEventListener('change', event => {
        onInputChange(event)
      })
      input.checked = answer.default ? 'true' : null
      label.appendChild(input)
      label.appendChild(document.createTextNode(answer.name))
      inputs[questionName].push(input)

      if (Array.isArray(answer.notes)) {
        label.appendChild(document.createTextNode(' — '))
        label.appendChild(document.createTextNode(answer.notes.join(', ')))
      }
    }

    if (after) {
      const paragraph = document.createElement('p')
      paragraph.className = 'after'
      paragraph.innerHTML = after
      set.appendChild(paragraph)
    }

    if (guide) {
      const details = document.createElement('details')
      const summary = document.createElement('summary')
      summary.appendChild(document.createTextNode('Notes'))
      details.appendChild(summary)
      details.insertAdjacentHTML('beforeend', guide)
      set.appendChild(details)
    }
  }

  if (haveAdvancedQuestions) form.appendChild(advancedQuestions)

  // Form Submit Button
  submitButton = document.createElement('button')
  form.appendChild(submitButton)
  submitButton.id = 'submit'
  submitButton.type = 'submit'
  submitButton.appendChild(document.createTextNode('Download ZIP Archive'))
  form.addEventListener('submit', event => {
    event.preventDefault()
    updateAnswersGlobal()
    const view = {}
    for (const key in answers) {
      const value = answers[key]
      view[`${key}: ${value}`] = true
    }
    Promise.all([
      renderFormTemplate(orderTemplate, view, titles.order, signatures, blanksValues),
      renderFormTemplate(termsTemplate, view, titles.terms),
      renderFormTemplate(termSheetTemplate, termSheetView(questions, answers), titles['term sheet'])
    ])
      .then(files => {
        const zip = new JSZip()
        zip.file(orderFileName, files[0])
        zip.file(termsFileName, files[1])
        zip.file(termSheetFileName, files[2])
        zip.file('Webpage.html', mustache.render(webpageTemplate, {
          project: titles.project,
          version: displayVersion,
          permalink: version === 'Development Draft'
            ? `https://${website}`
            : `https://${website}/versions/${version}`,
          answers: Object.keys(answers)
            .filter(questionName => answers[questionName])
            .map(questionName => {
              return {
                question: questionName,
                answer: answers[questionName]
              }
            })
        }))
        zip.generateAsync({ type: 'blob' })
          .then(blob => {
            fileSaver.saveAs(blob, `${titles.project}.zip`, true)
          })
      })
  })

  // Post-Download Instructions
  const instructions = [
    `Make sure to fill out <code>${orderFileName}</code> with the details for your deal.`,
    `The legal terms in <code>${termsFileName}</code> do not have any blanks to fill.`
  ]
  for (const text of instructions) {
    const paragraph = document.createElement('p')
    paragraph.innerHTML = text
    form.appendChild(paragraph)
  }

  onInputChange()

  main.replaceChildren(fragment)
})

function renderFormTemplate (template, view, title, signatures, blanksArgument = {}) {
  const blanksValues = {
    Version: version,
    Website: website
  }
  Object.assign(blanksValues, blanksArgument)
  const markdown = mustache.render(template, view)
  const parsed = commonmark.parse(markdown)
  const options = { title: `${titles.project} ${title}` }
  options.edition = displayVersion
  Object.assign(options, docxOptions)
  const instructions = prepareBlanks(blanksValues, parsed.directions)
  if (signatures) options.after = ooxmlSignaturePages(signatures)
  return docx(parsed.form, instructions, options).generateAsync({ type: 'blob' })
}

function onInputChange () {
  updateAnswersGlobal()
  applyQuestionRequirements()
}

function updateAnswersGlobal () {
  for (const { name: questionName } of questions) {
    answers[questionName] = null
    const elements = inputs[questionName]
    for (const element of elements) {
      if (element.checked && !element.disabled) {
        answers[questionName] = element.dataset.answer
      }
    }
  }
}

function applyQuestionRequirements () {
  const requiredQuestionNames = []
  for (const { name: questionName, requirements } of questions) {
    if (!Array.isArray(requirements)) {
      requiredQuestionNames.push(questionName)
      continue
    }
    const met = requirements.some(requirement => {
      const requiredQuestionName = Object.keys(requirement)[0]
      const requiredAnswerName = requirement[requiredQuestionName]
      return answers[requiredQuestionName] === requiredAnswerName
    })
    if (met) {
      requiredQuestionNames.push(questionName)
      fieldsets[questionName].classList.remove('hidden')
      for (const input of inputs[questionName]) input.disabled = false
    } else {
      fieldsets[questionName].classList.add('hidden')
      for (const input of inputs[questionName]) {
        input.disabled = true
        input.checked = false
      }
    }
  }
  const complete = requiredQuestionNames.every(questionName => answers[questionName])
  submitButton.disabled = !complete
}
